import { useState, useEffect } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { Mail, Plus, Trash2, Copy, Check, AlertCircle, CheckCircle2, Eye, EyeOff, Pencil, ExternalLink, LifeBuoy } from 'lucide-react';

interface EmailRule {
  id: string;
  from: string;
  to: string;
  name: string;
}

interface ReservedAddress {
  address: string;
  reason: string;
}

interface SmtpConfig {
  configured: boolean;
  domain: string;
  smtp: {
    host: string;
    port: number;
    username: string;
    password: string;
  };
}

interface EmailDomainStatus {
  configured: boolean;
  domain: string;
  status: string;
  verified: boolean;
  records: { type: string; name: string; value: string; priority?: number; status?: string }[];
  dnsManaged: boolean | null;
}

export default function EmailSetup() {
  const [rules, setRules] = useState<EmailRule[]>([]);
  const [reserved, setReserved] = useState<ReservedAddress[]>([]);
  const [domain, setDomain] = useState('');
  const [configured, setConfigured] = useState(false);
  const [zoneFound, setZoneFound] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Rule modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<EmailRule | null>(null);
  const [formPrefix, setFormPrefix] = useState('');
  const [formTo, setFormTo] = useState('');

  // Cloudflare config form (shown when !configured)
  const [cfToken, setCfToken] = useState('');
  const [cfAccountId, setCfAccountId] = useState('');
  const [cfAuthEmail, setCfAuthEmail] = useState('');
  const [configSaving, setConfigSaving] = useState(false);

  // SMTP config
  const [smtpConfig, setSmtpConfig] = useState<SmtpConfig | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Resend sending/receiving domain verification status
  const [domainStatus, setDomainStatus] = useState<EmailDomainStatus | null>(null);

  useEffect(() => {
    loadRules();
    loadSmtpConfig();
    loadDomainStatus();
  }, []);

  async function loadDomainStatus() {
    try {
      const data = await api.get<EmailDomainStatus>('/admin/email-setup/domain-status');
      setDomainStatus(data);
    } catch {
      // Domain status is best-effort — don't block the page on it.
    }
  }

  async function loadRules() {
    try {
      const data = await api.get<{ rules: EmailRule[]; reserved?: ReservedAddress[]; domain: string; configured: boolean; zoneFound?: boolean }>('/admin/email-setup');
      setRules(data.rules);
      setReserved(data.reserved || []);
      setDomain(data.domain);
      setConfigured(data.configured);
      setZoneFound(data.zoneFound !== false);
    } catch {
      setError('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  }

  async function loadSmtpConfig() {
    try {
      const data = await api.get<SmtpConfig>('/admin/email-setup/smtp-config');
      setSmtpConfig(data);
    } catch {
      // SMTP config is optional
    }
  }

  async function handleSaveCloudflareConfig(e: React.FormEvent) {
    e.preventDefault();
    if (!cfToken || !cfAccountId) return;
    setConfigSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/admin/credentials/CLOUDFLARE_API_TOKEN', { value: cfToken });
      await api.put('/admin/credentials/CLOUDFLARE_ACCOUNT_ID', { value: cfAccountId });
      if (cfAuthEmail) {
        await api.put('/admin/credentials/CLOUDFLARE_AUTH_EMAIL', { value: cfAuthEmail });
      }
      setSuccess('Cloudflare credentials saved.');
      setCfToken('');
      setCfAccountId('');
      setCfAuthEmail('');
      setLoading(true);
      await loadRules();
    } catch (err: any) {
      setError(err.message || 'Failed to save Cloudflare credentials');
    } finally {
      setConfigSaving(false);
    }
  }

  function openNewRule() {
    setEditingRule(null);
    setFormPrefix('');
    setFormTo('');
    setModalOpen(true);
  }

  function openEditRule(rule: EmailRule) {
    setEditingRule(rule);
    const prefix = rule.from.includes('@') ? rule.from.split('@')[0] : rule.from;
    setFormPrefix(prefix);
    setFormTo(rule.to);
    setModalOpen(true);
  }

  async function handleSaveRule(e: React.FormEvent) {
    e.preventDefault();
    if (!formPrefix || !formTo) return;

    const from = `${formPrefix}@${domain}`;
    if (reserved.some((r) => r.address.toLowerCase() === from.toLowerCase())) {
      setError(`${from} is reserved for the Axiss support agent and can't be used as a forwarding address.`);
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (editingRule) {
        const result = await api.post<{ success: boolean; steps?: any[]; error?: string }>(
          '/admin/email-setup/rules/update',
          { id: editingRule.id, oldFrom: editingRule.from, from, to: formTo },
        );
        if (result.success) {
          setSuccess(`Forwarding rule updated: ${from} -> ${formTo}`);
          setModalOpen(false);
          await loadRules();
        } else {
          const failedStep = result.steps?.find((s: any) => s.status === 'failed');
          setError(failedStep?.message || result.error || 'Failed to update forwarding rule');
        }
      } else {
        const result = await api.post<{ success: boolean; steps?: any[] }>(
          '/admin/email-setup/rules',
          { from, to: formTo },
        );
        if (result.success) {
          setSuccess(`Forwarding rule created: ${from} -> ${formTo}`);
          setModalOpen(false);
          await loadRules();
        } else {
          const failedStep = result.steps?.find((s: any) => s.status === 'failed');
          setError(failedStep?.message || 'Failed to create forwarding rule');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save forwarding rule');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteRule(from: string) {
    if (!confirm(`Delete forwarding rule for ${from}?`)) return;
    setDeleting(from);
    setError('');
    setSuccess('');

    try {
      await api.post('/admin/email-setup/rules/delete', { from });
      setSuccess(`Forwarding rule removed: ${from}`);
      await loadRules();
    } catch (err: any) {
      setError(err.message || 'Failed to delete forwarding rule');
    } finally {
      setDeleting(null);
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Email Setup</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage email forwarding and configure sending for {domain}
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          {success}
        </div>
      )}

      {/* Section 0: Sending & receiving domain verification. Gate on a real
          domain-status payload (verified is a boolean) so a partial response
          never renders a half-built card. */}
      {domainStatus?.configured && typeof domainStatus.verified === 'boolean' && (
        <Card title="Sending & Receiving Domain" padding="lg">
          {domainStatus.verified ? (
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>
                <strong>{domainStatus.domain}</strong> is verified — your store can send and receive email from this domain.
              </span>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-sm text-amber-800">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                <span>
                  <strong>{domainStatus.domain}</strong> isn&rsquo;t fully verified yet
                  {domainStatus.status && domainStatus.status !== 'unknown' ? ` (status: ${domainStatus.status})` : ''}.{' '}
                  {domainStatus.dnsManaged
                    ? "We've added the required DNS records for you — they can take a few minutes to propagate. This section will clear once it verifies."
                    : "Add the DNS records below at your DNS provider (the service where your domain's DNS is managed). It will verify automatically once they resolve."}
                </span>
              </div>
              {domainStatus.records.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">Type</th>
                        <th className="px-3 py-2 font-medium">Name / Host</th>
                        <th className="px-3 py-2 font-medium">Value</th>
                        <th className="px-3 py-2 font-medium">Priority</th>
                        <th className="px-3 py-2 font-medium sr-only">Copy</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 font-mono text-gray-800">
                      {domainStatus.records.map((r, i) => (
                        <tr key={i} className="align-top">
                          <td className="px-3 py-2">{r.type}</td>
                          <td className="px-3 py-2 break-all">{r.name}</td>
                          <td className="px-3 py-2 break-all">{r.value}</td>
                          <td className="px-3 py-2">{r.type === 'MX' && r.priority != null ? r.priority : '—'}</td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => copyToClipboard(r.value, `dns-${i}`)}
                              className="text-gray-400 hover:text-gray-600 p-0.5"
                              aria-label={`Copy value for ${r.type} ${r.name}`}
                            >
                              {copied === `dns-${i}` ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Section 1: Email Forwarding Rules */}
      <Card title="Email Addresses" padding="lg">
        {!configured ? (
          <form onSubmit={handleSaveCloudflareConfig} className="space-y-4">
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                Cloudflare Email Routing hasn't been configured for this site yet. Paste a Cloudflare API Token
                and your Account ID below — we'll save them securely and use them to manage forwarding rules.
              </p>
              <p className="text-xs text-gray-500">
                Need a token?{' '}
                <a
                  href="https://developers.cloudflare.com/fundamentals/api/get-started/create-token/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-green-700 hover:underline inline-flex items-center gap-0.5"
                >
                  Create a scoped API token <ExternalLink className="h-3 w-3" />
                </a>{' '}
                with <code className="bg-gray-100 px-1 rounded">Zone:Edit</code>,{' '}
                <code className="bg-gray-100 px-1 rounded">Email Routing:Edit</code>, and{' '}
                <code className="bg-gray-100 px-1 rounded">Account:Email Routing Addresses:Edit</code> permissions.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="API Token"
                type="password"
                value={cfToken}
                onChange={(e) => setCfToken(e.target.value)}
                placeholder="cfut_xxxxxxxx..."
                required
                autoComplete="off"
              />
              <Input
                label="Account ID"
                value={cfAccountId}
                onChange={(e) => setCfAccountId(e.target.value)}
                placeholder="32-char hex string"
                required
                autoComplete="off"
              />
            </div>
            <Input
              label="Auth Email (optional — only for legacy Global API Keys)"
              type="email"
              value={cfAuthEmail}
              onChange={(e) => setCfAuthEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="off"
            />
            <Button type="submit" variant="primary" size="sm" disabled={configSaving || !cfToken || !cfAccountId}>
              {configSaving ? 'Saving...' : 'Save & Continue'}
            </Button>
          </form>
        ) : !zoneFound ? (
          <div className="text-sm text-gray-500">
            <p>Domain <strong>{domain}</strong> was not found in your Cloudflare account. Make sure the domain is added to Cloudflare with email routing enabled.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-gray-500">
                Email forwarding rules route incoming email to personal inboxes via Cloudflare Email Routing.
              </p>
              <Button variant="primary" size="sm" onClick={openNewRule}>
                <Plus className="h-4 w-4 mr-1" /> Add Email
              </Button>
            </div>

            {reserved.length > 0 && (
              <div className="border border-amber-200 bg-amber-50/40 rounded-lg divide-y divide-amber-100">
                {reserved.map((r) => (
                  <div key={r.address} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <LifeBuoy className="h-4 w-4 text-amber-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{r.address}</p>
                        <p className="text-xs text-gray-500 truncate">{r.reason}</p>
                      </div>
                    </div>
                    <span className="text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full shrink-0">
                      Reserved
                    </span>
                  </div>
                ))}
              </div>
            )}

            {rules.length > 0 ? (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                {rules.map((rule) => (
                  <div key={rule.id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{rule.from}</p>
                        <p className="text-xs text-gray-500 truncate">Forwards to {rule.to}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openEditRule(rule)}
                        className="text-gray-400 hover:text-green-600 p-1 rounded"
                        aria-label={`Edit ${rule.from}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.from)}
                        disabled={deleting === rule.from}
                        className="text-gray-400 hover:text-red-500 p-1 rounded disabled:opacity-50"
                        aria-label={`Delete ${rule.from}`}
                      >
                        {deleting === rule.from ? <LoadingSpinner size="sm" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No email forwarding rules configured yet.</p>
            )}
          </div>
        )}
      </Card>

      {/* Rule editor modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingRule ? 'Edit Forwarding Rule' : 'Add Forwarding Rule'}
      >
        <form onSubmit={handleSaveRule} className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Email address</label>
            <div className="flex">
              <input
                type="text"
                value={formPrefix}
                onChange={(e) => setFormPrefix(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
                placeholder="e.g., hello"
                className="flex-1 min-w-0 rounded-l-lg border border-r-0 border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
                autoFocus
              />
              <span className="inline-flex items-center px-3 bg-gray-100 border border-gray-300 rounded-r-lg text-sm text-gray-500">
                @{domain}
              </span>
            </div>
          </div>
          <Input
            label="Forward to"
            type="email"
            value={formTo}
            onChange={(e) => setFormTo(e.target.value)}
            placeholder="personal@gmail.com"
            required
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving || !formPrefix || !formTo}>
              {saving ? 'Saving...' : editingRule ? 'Save Changes' : 'Add Rule'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Section 2: Gmail "Send As" Setup */}
      <Card title="Send Email from Gmail" padding="lg">
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            After setting up a forwarding rule above, you can also <strong>send</strong> emails from your custom domain using Gmail.
            This uses Resend's SMTP service so emails appear to come from your {domain} address.
          </p>

          {smtpConfig?.configured ? (
            <>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">SMTP Credentials</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Server:</span>
                    <div className="flex items-center gap-1">
                      <code className="font-mono text-gray-900">{smtpConfig.smtp.host}</code>
                      <button onClick={() => copyToClipboard(smtpConfig.smtp.host, 'host')} className="text-gray-400 hover:text-gray-600 p-0.5">
                        {copied === 'host' ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Port:</span>
                    <div className="flex items-center gap-1">
                      <code className="font-mono text-gray-900">{smtpConfig.smtp.port}</code>
                      <button onClick={() => copyToClipboard(String(smtpConfig.smtp.port), 'port')} className="text-gray-400 hover:text-gray-600 p-0.5">
                        {copied === 'port' ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Username:</span>
                    <div className="flex items-center gap-1">
                      <code className="font-mono text-gray-900">{smtpConfig.smtp.username}</code>
                      <button onClick={() => copyToClipboard(smtpConfig.smtp.username, 'username')} className="text-gray-400 hover:text-gray-600 p-0.5">
                        {copied === 'username' ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Password:</span>
                    <div className="flex items-center gap-1">
                      <code className="font-mono text-gray-900">
                        {showPassword ? smtpConfig.smtp.password : '••••••••••••'}
                      </code>
                      <button onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-600 p-0.5" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                        {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={() => copyToClipboard(smtpConfig.smtp.password, 'password')} className="text-gray-400 hover:text-gray-600 p-0.5">
                        {copied === 'password' ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Gmail Setup Steps</h3>
                <ol className="text-sm text-gray-600 space-y-2.5 list-decimal list-inside">
                  <li>Open <strong>Gmail Settings</strong> (gear icon) &rarr; <strong>See all settings</strong> &rarr; <strong>Accounts and Import</strong> tab</li>
                  <li>In the <strong>"Send mail as"</strong> section, click <strong>"Add another email address"</strong></li>
                  <li>Enter your name and your <code className="bg-gray-100 px-1 rounded">@{domain}</code> email address, then uncheck "Treat as an alias"</li>
                  <li>
                    Enter the SMTP settings shown above:
                    <ul className="ml-5 mt-1 space-y-0.5 list-disc text-gray-500">
                      <li>SMTP Server: <code className="bg-gray-100 px-1 rounded">{smtpConfig.smtp.host}</code></li>
                      <li>Port: <code className="bg-gray-100 px-1 rounded">{smtpConfig.smtp.port}</code> (TLS)</li>
                      <li>Username: <code className="bg-gray-100 px-1 rounded">{smtpConfig.smtp.username}</code></li>
                      <li>Password: use the password shown above (click the eye icon to reveal, then copy)</li>
                    </ul>
                  </li>
                  <li>Gmail will send a <strong>verification email</strong> to your <code className="bg-gray-100 px-1 rounded">@{domain}</code> address (which forwards to your personal inbox). Click the confirmation link.</li>
                  <li>
                    <strong>Enable automatic reply-from:</strong> In the same <strong>"Send mail as"</strong> section, select{' '}
                    <strong>"Reply from the same address the message was sent to"</strong>. This way, when someone emails your{' '}
                    <code className="bg-gray-100 px-1 rounded">@{domain}</code> address, Gmail will automatically reply from that address instead of your personal Gmail.
                  </li>
                  <li>(Optional) To send <em>new</em> emails from your <code className="bg-gray-100 px-1 rounded">@{domain}</code> address by default, click <strong>"make default"</strong> next to it in the "Send mail as" list.</li>
                </ol>
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  <strong>How it works:</strong> Your personal Gmail handles both addresses. Incoming email to your{' '}
                  <code className="bg-blue-100 px-1 rounded">@{domain}</code> address is forwarded to your Gmail inbox via Cloudflare.
                  Outgoing email is sent through Resend's SMTP servers so it appears to come from your{' '}
                  <code className="bg-blue-100 px-1 rounded">@{domain}</code> address. With "Reply from the same address" enabled,
                  Gmail automatically picks the right sender address when replying.
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500">
              <p>Resend SMTP is not configured. Add <code className="bg-gray-100 px-1 rounded">RESEND_API_KEY</code> to your server environment variables — or save it under <a href="/admin/credentials" className="text-green-700 hover:underline">Platform Credentials</a> — to enable sending via Gmail.</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
